import { useSessionMessageApi } from '../../utils/actionableNotifications';
import { useCallback, useEffect, useRef, useState } from 'react';


// Commit controlled values only after acceptance. Failed saves retain saved values.
export default function useInstantSave(scope) {
  const message = useSessionMessageApi();
  const [pending, setPending] = useState({});
  const flights = useRef(new Map());
  const active = useRef({ scope, mounted: true });
  active.current.scope = scope;
  useEffect(() => {
    const lifecycle = active.current;
    lifecycle.mounted = true;
    const requests = flights.current;
    return () => { lifecycle.mounted = false; requests.clear(); };
  }, []);
  const save = useCallback(async (id, next, apply) => {
    if (flights.current.has(id)) return false;
    const ticket = Symbol(id), startedScope = active.current.scope;
    flights.current.set(id, ticket);
    setPending(current => ({ ...current, [id]: true }));
    const current = () => active.current.mounted && active.current.scope === startedScope;
    try {
      const result = await apply(next);
      if (result === false || result === 'changed') throw new Error('Settings were not saved. Review the current values and try again.');
      if (current()) message.success('Saved');
      return current();
    } catch (error) {
      if (current()) message.error(error?.response?.data?.message || error?.message || 'Unable to save settings.');
      return false;
    } finally {
      if (flights.current.get(id) === ticket) flights.current.delete(id);
      if (active.current.mounted) setPending(value => { const copy = { ...value }; delete copy[id]; return copy; });
    }
  }, [message]);
  return { saving: id => Boolean(pending[id]), save };
}
